import axios from "axios";
import {
  getAccessToken,
  setSessionToken,
  clearSessionToken,
  getUserTrackingInfo,
} from "@/utils/auth";
import { getEnvVar } from "@/utils/util";
import { logoutUser } from "@/utils/amplitude";
import { userLoggedOut } from "@/utils/tracking";
import { rememberReturnPath } from "@/utils/postLoginRedirect";

const baseURL = getEnvVar("API_BASE_URL", true);
const API_BASE = getEnvVar("API_BASE");

// Create a base axios instance for public API calls
export const publicAxios = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Response interceptor for publicAxios to handle rate limiting
publicAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 429 Too Many Requests errors (rate limiting)
    if (error.response?.status === 429) {
      // Create a custom error object with rate limit flag
      const rateLimitError = {
        ...error,
        isRateLimitError: true,
        message: error.response?.data?.detail || "Too many requests. Please try again later.",
      };
      return Promise.reject(rateLimitError);
    }
    return Promise.reject(error);
  }
);

// Create an axios instance for authenticated API calls
export const authAxios = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token to requests
authAxios.interceptors.request.use(
  (config) => {
    // A caller-supplied Authorization header WINS. This used to overwrite it
    // unconditionally, which silently broke every "use this token, not the
    // session's" call — api.users.getCurrentUser(token) and
    // getOnboardingStep(token). The impersonation flow is where it bit:
    // fwd_token is one cookie shared by every tab, so opening an admin's
    // one-time login link in a second tab left the ADMIN's token in the
    // cookie. verifyLoginToken passed the target user's fresh token
    // explicitly, this interceptor replaced it with the admin's, and
    // /users/me came back describing the ADMIN. The session then ran with the
    // admin as `user` but the target's token on the wire, so the landing page
    // rendered for the admin's roles and its first role-gated request 403'd —
    // and the handler below hard-redirected to /unauthorized. That redirect is
    // a full page load, which re-read the cookie correctly, which is why
    // "Go to Home" then worked.
    if (!config.headers.has("Authorization")) {
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor to handle auth errors
interface FailedQueueItem {
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
}

let failedQueue: FailedQueueItem[] = [];
let isRefreshing = false;

// Helper function to handle automatic logout tracking
function handleAutomaticLogout(redirectPath: string) {
  // Track session timeout before clearing tokens
  const userInfo = getUserTrackingInfo();
  if (userInfo) {
    userLoggedOut({
      user_id: userInfo.id,
      user_role: userInfo.role,
      logout_method: "timeout",
    });
  }

  // DISABLED (multi-account): also deleted this user's fwd_token_<id> cookie so
  // the switcher in other tabs showed them as "Session expired".
  clearSessionToken();
  logoutUser();
  window.location.href = redirectPath;
}

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
}

authAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 429 Too Many Requests errors (rate limiting) - DON'T retry
    if (error.response?.status === 429) {
      const rateLimitError = {
        ...error,
        isRateLimitError: true,
        message: error.response?.data?.detail || "Daily limit reached. Please try again tomorrow.",
      };
      return Promise.reject(rateLimitError);
    }
    
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = "Bearer " + token;
            return authAxios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }
      isRefreshing = true;
      try {
        const response = await publicAxios.post(
          `${API_BASE}/auth/refresh`,
          {}, // empty body
          {
            withCredentials: true,
            headers: {
              Authorization: `Bearer ${getAccessToken()}`,
            },
          },
        );
        const { accessToken } = response.data;
        // Writes the shared fwd_token cookie, so the refreshed token is picked
        // up by every other tab too — no per-user copy to keep in sync.
        setSessionToken(accessToken);
        processQueue(null, accessToken);
        originalRequest.headers["Authorization"] = "Bearer " + accessToken;
        return authAxios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        // Don't redirect to /login if we're on a public lead route
        const currentPath = window.location.pathname;
        const publicLeadRoutes = ['/leads/email-sent', '/leads/verify', '/leads/matching'];
        const isPublicLeadRoute = publicLeadRoutes.some(route => currentPath.startsWith(route));

        if (!isPublicLeadRoute) {
          // Clear this tab's session and do a clean logout.
          // Never reload — reloading with an expired token causes an infinite
          // 401 → refresh → fail → reload loop when multiple tabs are open.
          // Come back to this page after signing in (e.g. an event opened
          // from an email while signed out) — see utils/postLoginRedirect.
          rememberReturnPath(currentPath + window.location.search);
          handleAutomaticLogout("/login");
          return Promise.reject({ isAuthRedirect: true, redirectTo: "/login" });
        } else {
          // Just clear this tab's session without redirecting on public lead routes
          clearSessionToken();
          return Promise.reject(refreshError);
        }
      } finally {
        isRefreshing = false;
      }
    }
    // Handle 403 Forbidden errors
    if (error.response?.status === 403) {
      // Callers can set _skipForbiddenRedirect: true to handle 403 themselves
      // without triggering a global redirect (e.g. optional/advisory-only endpoints).
      if (originalRequest._skipForbiddenRedirect) {
        return Promise.reject(error);
      }

      // Don't redirect to /unauthorized if we're on a public lead route, wrapup flow,
      // or already on /unauthorized itself. The last case is critical: UnauthorizedPage
      // makes its own API calls (e.g. pab-sessions/me) that can 403 for advisors with no
      // PAB — without this guard, each such 403 fires window.location.href="/unauthorized"
      // again, reloading the page in an infinite loop.
      const currentPath = window.location.pathname;
      const bypassRoutes = ['/leads/email-sent', '/leads/verify', '/leads/matching', '/wrapup', '/unauthorized'];
      const isBypassRoute = bypassRoutes.some(route => currentPath.startsWith(route));

      if (!isBypassRoute) {
        window.location.href = "/unauthorized";
        // Return a rejected promise to stop the request chain
        return Promise.reject({ isAuthRedirect: true, redirectTo: "/unauthorized" });
      } else {
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);
